import {
  faFaceFrown,
  faRightLeft,
  faTrophy,
} from '@fortawesome/free-solid-svg-icons';
import { useMediaQuery, useTheme } from '@mui/material';
import { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getAllInstruments } from '!core/redux/userInstruments/userInstruments.selectors';
import { TrlFilters, TrlSectionTitle } from '»components/atoms';
import { TrlGrid } from '»components/molecules';
import { useGridRemainingHeight } from '»utils/customHooks';
import { TrlFormattedMessagePlain } from '»utils/i18n';

import { TrlTopInstrumentsMobile } from './Mobile';
import { columns } from './trlTopInstruments.schema';
import {
  getInstrTopGroups,
  getSelectedInstrTopGroup,
  getTopInstruments,
} from './trlTopInstruments.selectors';
import { trlTopInstrumentsSlice } from './trlTopInstruments.slice';
import { TopInstrumentsContainer } from './TrlTopInstruments.styles';

export const TrlTopInstruments = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const topInstrumentsContainerRef = useRef(null);

  const allInstruments = useSelector(getAllInstruments);
  const instrTopGroups = useSelector(getInstrTopGroups);
  const selectedInstrTopGroup = useSelector(getSelectedInstrTopGroup);
  const topInstruments = useSelector(getTopInstruments);

  const topInstrFilters = [
    {
      value: 'QTDMAIS',
      label: TrlFormattedMessagePlain({
        id: 'TRLT_UI-MOST_TRADED',
      }),
      icon: faRightLeft,
    },
    {
      value: 'COTMAIS',
      label: TrlFormattedMessagePlain({ id: 'TRLT_UI-WINNERS' }),
      icon: faTrophy,
    },
    {
      value: 'COTMENOS',
      label: TrlFormattedMessagePlain({ id: 'TRLT_UI-LOSERS' }),
      icon: faFaceFrown,
    },
  ];

  const [selectedInstrFilter, setSelectedInstrFilter] = useState(
    topInstrFilters[0].value,
  );

  const displayedTopInstrs = topInstruments
    .filter((instr) => selectedInstrFilter === instr.topType)
    .sort(function compareNumbers(a, b) {
      return a.topOrder - b.topOrder;
    })
    .reduce((acc, topInstr) => {
      const topInstrRoundPrice = allInstruments.find(
        (instrument) =>
          instrument.exchange === topInstr.exchangeId &&
          instrument.symbol === topInstr.symbol &&
          instrument.securityType === topInstr.securityType,
      )?.roundPrice;

      acc.push({ ...topInstr, roundPrice: topInstrRoundPrice });

      return acc;
    }, []);

  const getRemainingHeight = useCallback(
    ({ container, gridContent, rowsHeight }) => {
      const gridContentTop = gridContent?.getBoundingClientRect().top;
      const containerBottom = container.getBoundingClientRect().bottom;

      const availableHeight =
        typeof gridContentTop === 'number'
          ? containerBottom - gridContentTop
          : gridContent?.clientHeight || 0;

      return availableHeight - rowsHeight - 4;
    },
    [],
  );

  const { remainingGridHeight, hasHorizontalScroll, hasVerticalScroll } =
    useGridRemainingHeight({
      containerRef: topInstrumentsContainerRef,
      gridDataLength: displayedTopInstrs.length,
      customGridRoot: document.querySelector(
        '#topInstrumentsContainer > .k-grid',
      ),
      getRemainingHeight,
    });

  const handleChangeInstrFilter = (_, newFilter) => {
    if (newFilter !== null) setSelectedInstrFilter(newFilter);
  };

  if (isMobile)
    return (
      <TrlTopInstrumentsMobile
        topInstruments={displayedTopInstrs}
        filters={topInstrFilters}
        selectedFilter={selectedInstrFilter}
        handleChangeFilter={handleChangeInstrFilter}
      />
    );

  return (
    <TopInstrumentsContainer
      id="topInstrumentsContainer"
      ref={topInstrumentsContainerRef}
    >
      <TrlSectionTitle
        title={TrlFormattedMessagePlain({ id: 'TRLT_UI-TOP5' })}
        hasPadding
        hasBackground
        currentValue={selectedInstrTopGroup}
        options={instrTopGroups}
        handleChangeOption={(data) => {
          dispatch(
            trlTopInstrumentsSlice.actions.selectInstrTopGroup({
              instrumentTopGroup: data,
            }),
          );
        }}
        otherBackgroundColor={theme.palette.colors.primaryBackground}
        isTopInstrumentsOptions
      />
      <TrlFilters
        selectedFilter={selectedInstrFilter}
        filters={topInstrFilters}
        handleChangeFilter={handleChangeInstrFilter}
        hasCustomStyles
      />
      <TrlGrid
        columns={columns()}
        data={displayedTopInstrs}
        noBorders
        hasTransparentBackground
        noMarginTop
        dataItemKey="symbol"
        otherGridColor={theme.palette.colors.primaryBackground}
        otherScrollColor={theme.palette.colors.primaryBackground}
        otherFirefoxSecScrollColor={theme.palette.colors.infoBackground}
        remainingGridHeight={remainingGridHeight}
        hasHorizontalScroll={hasHorizontalScroll}
        hasVerticalScroll={hasVerticalScroll}
      />
    </TopInstrumentsContainer>
  );
};
